import React, { useState, useEffect, useCallback } from 'react';
import { spotsApi } from '../../api/client';
import { FeedCard } from '../../components/FeedCard/FeedCard';
import { spotApiToSpot } from '../../context/DataContext';
import { Spot } from '../../types';
import styles from './Favorites.module.css';

type Category = 'all' | 'liked' | 'beautiful' | 'cool' | 'sweetPhoto';

const CATEGORIES: { key: Category; label: string; icon: string }[] = [
  { key: 'all', label: 'All', icon: '★' },
  { key: 'liked', label: 'Liked', icon: '♥' },
  { key: 'beautiful', label: 'Beautiful', icon: '✨' },
  { key: 'cool', label: 'Cool', icon: '😎' },
  { key: 'sweetPhoto', label: 'Sweet Photo', icon: '📷' },
];

export function Favorites() {
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [collections, setCollections] = useState<{
    all: Spot[];
    liked: Spot[];
    beautiful: Spot[];
    cool: Spot[];
    sweetPhoto: Spot[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFavorites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allSpots = (await spotsApi.list()).map(spotApiToSpot);
      const liked = allSpots.filter((spot) => (spot.likes ?? []).length > 0);
      const beautiful = allSpots.filter((spot) => (spot.beautiful ?? []).length > 0);
      const cool = allSpots.filter((spot) => (spot.cool ?? []).length > 0);
      const sweetPhoto = allSpots.filter((spot) => (spot.sweetPhoto ?? []).length > 0);
      const all = allSpots.filter(
        (spot) =>
          (spot.likes ?? []).length > 0 ||
          (spot.beautiful ?? []).length > 0 ||
          (spot.cool ?? []).length > 0 ||
          (spot.sweetPhoto ?? []).length > 0
      );
      setCollections({
        all,
        liked,
        beautiful,
        cool,
        sweetPhoto,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load favorites');
      setCollections({
        all: [],
        liked: [],
        beautiful: [],
        cool: [],
        sweetPhoto: [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const spots = collections ? collections[activeCategory] : [];
  const counts = collections
    ? {
        all: collections.all.length,
        liked: collections.liked.length,
        beautiful: collections.beautiful.length,
        cool: collections.cool.length,
        sweetPhoto: collections.sweetPhoto.length,
      }
    : null;

  return (
    <div className={styles.page}>
      <h1>Favorites</h1>
      <p className={styles.subtitle}>
        Browse every spot the RideSpot community has tagged as liked, beautiful, cool, or sweet photo
      </p>

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          <div className={styles.tabs}>
            {CATEGORIES.map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                className={activeCategory === key ? styles.tabActive : styles.tab}
                onClick={() => setActiveCategory(key)}
              >
                {icon} {label}
                {counts != null && <span className={styles.tabCount}> ({counts[key]})</span>}
              </button>
            ))}
          </div>

          <div className={styles.feed}>
            {spots.length === 0 ? (
              <div className={styles.empty}>
                <p>
                  {activeCategory === 'all' && 'No tagged spots yet.'}
                  {activeCategory === 'liked' && 'No liked spots yet.'}
                  {activeCategory === 'beautiful' && 'No “beautiful” spots yet.'}
                  {activeCategory === 'cool' && 'No “cool” spots yet.'}
                  {activeCategory === 'sweetPhoto' && 'No “sweet photo” spots yet.'}
                </p>
              </div>
            ) : (
              spots.map((spot) => <FeedCard key={spot.id} spot={spot} compact />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
